/* eslint-disable @typescript-eslint/no-unused-vars */
'use client'

import dynamic from 'next/dynamic'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
// import { SiteHeader } from '@/components/site-header'
import { useSidebar } from '@/components/ui/sidebar'
import {
    RightSidebar,
    // RightPanelTrigger,
    useRightSidebar,
} from '@/components/ui/right-sidebar'
import { ResizablePanel, ResizableHandle } from '@/components/ui/resizable'
import { ImperativePanelHandle } from 'react-resizable-panels'
import { useAuth } from '@/lib/auth'
import { NavUser } from '@/components/nav-user'
import { plans } from '@/lib/billingsdk-config'
import { AnalysisCacheProvider } from '@/contexts/analysis-cache-context'
import { LayoutProviders } from '@/components/layout-providers'
import { MainContent } from '@/components/layouts/main-content'
import { useIframeResize } from '@/hooks/use-iframe-resize'
import * as React from 'react'

// Dynamically import heavy components to reduce initial bundle size
const AppSidebar = dynamic(
    () => import('@/components/app-sidebar').then((mod) => ({ default: mod.AppSidebar })),
    { ssr: false }
)

const ResizablePanelGroup = dynamic(
    () => import('@/components/ui/resizable').then((mod) => ({ default: mod.ResizablePanelGroup })),
    { ssr: false }
)

const CompanyDialog = dynamic(
    () => import('@/components/company-dialog').then((mod) => ({ default: mod.CompanyDialog })),
    { ssr: false }
)

const UpdatePlanDialog = dynamic(
    () =>
        import('@/components/billingsdk/update-plan-dialog').then((mod) => ({
            default: mod.UpdatePlanDialog,
        })),
    { ssr: false }
)

// Right sidebar content context
type RightSidebarContentContextProps = {
    // `showCopyButton` allows consumers to enable the copy button in the RightSidebar
    setRightSidebarContent: (
        content: React.ReactNode,
        showCopyButton?: boolean,
        title?: string
    ) => void
    clearRightSidebarContent: () => void
    setRightSidebarWidth: (size: number) => void
}

const RightSidebarContentContext = React.createContext<RightSidebarContentContextProps | null>(null)

const DEFAULT_RIGHT_PANEL_SIZE = 25
const MAX_RIGHT_PANEL_SIZE = 50

export function useRightSidebarContent() {
    const context = React.useContext(RightSidebarContentContext)
    if (!context) {
        throw new Error('useRightSidebarContent must be used within a RightSidebarContentProvider')
    }
    return context
}

interface AppLayoutProps {
    children: React.ReactNode
}

function AuthenticatedLayoutContent({ children }: { children: React.ReactNode }) {
    const [rightSidebarContent, setRightSidebarContentState] = React.useState<React.ReactNode>(null)
    const [rightSidebarShowCopyButton, setRightSidebarShowCopyButton] =
        React.useState<boolean>(false)
    const [rightSidebarTitle, setRightSidebarTitle] = React.useState<string | undefined>(undefined)
    // simplebar support removed — always use the default scroll container
    const rightPanelRef = React.useRef<ImperativePanelHandle>(null)
    const mainPanelRef = React.useRef<ImperativePanelHandle>(null)
    const [isAnimating, setIsAnimating] = React.useState(false)
    const [shouldRenderContent, setShouldRenderContent] = React.useState(false)
    const rightPanelTargetSizeRef = React.useRef<number>(25)
    const [rightPanelSize, setRightPanelSize] = React.useState<number>(0)
    const router = useRouter()
    const pathname = usePathname()
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)

    // Track viewport width separately to avoid remounting when sidebar resizes
    // Initialize to a stable value that matches SSR to prevent hydration mismatch
    const [viewportWidth, setViewportWidth] = React.useState<number>(1024)
    const [isMounted, setIsMounted] = React.useState(false)

    // Initialize viewport width on mount and track window resize
    React.useEffect(() => {
        setIsMounted(true)

        const updateViewportWidth = () => {
            setViewportWidth(window.innerWidth)
        }

        // Set initial width
        updateViewportWidth()

        // Listen for window resize
        window.addEventListener('resize', updateViewportWidth)

        return () => {
            window.removeEventListener('resize', updateViewportWidth)
        }
    }, [])

    // Use viewport width to determine if we should show sidebar from bottom
    // This prevents remounting when the right sidebar resizes (which changes containerWidth)
    // During SSR and before mount, assume desktop layout to prevent hydration mismatch
    const isMobile = isMounted && viewportWidth < 700

    // Create a stable ID and key for ResizablePanelGroup to prevent hydration mismatches
    // Only change when company changes, not when tabs change within same company
    // Note: isMobile is NOT included in the key to prevent remounting when layout changes
    // The direction prop can change dynamically without remounting
    const { panelGroupKey, panelGroupId } = React.useMemo(() => {
        const segments = pathname.split('/').filter(Boolean)

        // For stock routes: /stock/[slug]/[tab]/[subtab]
        // Only use /stock/[slug] as key to prevent unmounting on tab changes
        if (segments[0] === 'stock' && segments[1]) {
            const baseKey = `/stock/${segments[1]}`
            return {
                panelGroupKey: `panel-group-${baseKey}`,
                panelGroupId: `resizable-${baseKey}`,
            }
        }

        // For other routes, use full pathname to force remount on route changes
        return {
            panelGroupKey: `panel-group-${pathname}`,
            panelGroupId: `resizable-${pathname}`,
        }
    }, [pathname])

    const setRightSidebarContent = React.useCallback(
        (content: React.ReactNode, showCopyButton: boolean = false, title?: string) => {
            setRightSidebarContentState(content)
            setRightSidebarShowCopyButton(showCopyButton)
            setRightSidebarTitle(title)
        },
        []
    )

    const clearRightSidebarContent = React.useCallback(() => {
        setRightSidebarContentState(null)
        setRightSidebarShowCopyButton(false)
        setRightSidebarTitle(undefined)
    }, [])
    const { open } = useRightSidebar()
    const { open: leftSidebarOpen, setOpen: setLeftSidebarOpen, state: sidebarState } = useSidebar()
    const [isLeftSidebarAutoCollapsed, setIsLeftSidebarAutoCollapsed] = React.useState(false)
    const [isLeftSidebarManuallyCollapsed, setIsLeftSidebarManuallyCollapsed] =
        React.useState(false)
    const rightPanelJustOpenedRef = React.useRef(false)
    const setRightSidebarWidth = React.useCallback(
        (size: number) => {
            // For vertical layout (mobile), use 95% of screen height by default
            const targetSize = isMobile ? 95 : size
            const maxSize = isMobile ? 95 : MAX_RIGHT_PANEL_SIZE
            const clampedSize = Math.min(Math.max(targetSize, 0), maxSize)
            rightPanelTargetSizeRef.current = clampedSize

            if (open && rightPanelRef.current) {
                rightPanelRef.current.resize(clampedSize)
                setRightPanelSize(clampedSize) // Update panel size state
                // For mobile, when right panel is 95%, main panel must be 5%
                if (isMobile && clampedSize === 95 && mainPanelRef.current) {
                    mainPanelRef.current.resize(5)
                }
            }
        },
        [open, isMobile]
    )
    const { user } = useAuth()
    const [isCompanyDialogOpen, setIsCompanyDialogOpen] = React.useState(false)
    const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = React.useState(false)

    // Prepare user data for NavUser
    const navUser = user
        ? {
              name: user.user_metadata?.['full_name'] || user.user_metadata?.['name'] || '',
              email: user.email || '',
              avatar:
                  user.user_metadata?.['avatar_url'] ||
                  user.user_metadata?.['picture'] ||
                  undefined,
          }
        : {
              name: '',
              email: 'guest@example.com',
              avatar: undefined,
          }

    interface StockData {
        symbol: string
        exchange?: string | null
        is_etf?: boolean
        is_fund?: boolean
    }

    // Handle company dialog body click
    const handleCompanyBodyClick = (stock: StockData) => {
        // Ensure we have valid exchange and symbol
        const exchange = stock.exchange || 'UNKNOWN'
        const symbol = stock.symbol || 'UNKNOWN'

        // Determine if this is a stock or fund based on the search result
        const isStock = !stock.is_etf && !stock.is_fund
        const routeType = isStock ? 'stock' : 'fund'

        const slug = `${exchange.toLowerCase()}-${symbol.toLowerCase()}`
        const path = `/${routeType}/${slug}/profile/overview`

        // Scroll to top before navigation
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'instant' })
        }

        router.push(path)
        setIsCompanyDialogOpen(false)
    }

    // Handle input focus to open company dialog
    const handleInputFocus = () => {
        setIsCompanyDialogOpen(true)
    }

    // Handle keyboard shortcut (Ctrl+K / Cmd+K) to open company dialog
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Check for Ctrl+K (Windows/Linux) or Cmd+K (Mac)
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault()
                setIsCompanyDialogOpen(true)
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    const handleUpgrade = () => {
        setIsUpgradeDialogOpen(true)
    }

    // Animate right panel when open state changes
    React.useEffect(() => {
        if (rightPanelRef.current) {
            setIsAnimating(true)
            let contentTimer: NodeJS.Timeout | undefined
            let rafId: number | undefined

            if (open) {
                // Mark that the panel just opened to prevent auto-expanding left sidebar
                rightPanelJustOpenedRef.current = true
                // When opening, show content immediately
                setShouldRenderContent(true)
                // Use requestAnimationFrame to ensure ref is updated if setRightSidebarWidth was called just before setOpen
                // This ensures the ref is read after all synchronous code has executed
                rafId = requestAnimationFrame(() => {
                    // For mobile (vertical), use 95% if target size is still default
                    // Otherwise use the target size that was set via setRightSidebarWidth
                    let targetSize = rightPanelTargetSizeRef.current
                    if (isMobile && rightPanelTargetSizeRef.current === DEFAULT_RIGHT_PANEL_SIZE) {
                        targetSize = 95
                        rightPanelTargetSizeRef.current = 95
                    }

                    // For mobile vertical layout, when right panel opens to 95%, main panel must be 5%
                    if (isMobile && targetSize === 95 && mainPanelRef.current) {
                        mainPanelRef.current.resize(5)
                    }

                    if (rightPanelRef.current) {
                        rightPanelRef.current.resize(targetSize)
                        setRightPanelSize(targetSize) // Update panel size state when opening
                    }
                })
            } else {
                // When closing, reset the flag
                rightPanelJustOpenedRef.current = false
                // When closing, hide content after animation
                rightPanelRef.current.resize(0)
                setRightPanelSize(0) // Update panel size state when closing
                // Reset main panel to 100% when closing in mobile
                if (isMobile && mainPanelRef.current) {
                    mainPanelRef.current.resize(100)
                }
                contentTimer = setTimeout(() => setShouldRenderContent(false), 500)
                rightPanelTargetSizeRef.current = DEFAULT_RIGHT_PANEL_SIZE
            }

            // Remove animation flag after transition completes
            const animTimer = setTimeout(() => setIsAnimating(false), 500)

            // Clear the "just opened" flag after animation completes to allow normal auto-expand behavior
            const clearJustOpenedTimer = setTimeout(() => {
                rightPanelJustOpenedRef.current = false
            }, 600) // Slightly longer than animation to ensure it's cleared after layout settles

            return () => {
                clearTimeout(animTimer)
                clearTimeout(clearJustOpenedTimer)
                if (contentTimer) clearTimeout(contentTimer)
                if (rafId !== undefined) {
                    cancelAnimationFrame(rafId)
                }
            }
        }
    }, [open, isMobile])

    // Adjust panel sizes when switching between mobile and desktop
    React.useEffect(() => {
        if (rightPanelRef.current && open) {
            // When switching to mobile, use 95% if setRightSidebarWidth was called
            if (isMobile && rightPanelTargetSizeRef.current !== DEFAULT_RIGHT_PANEL_SIZE) {
                rightPanelTargetSizeRef.current = 95
                rightPanelRef.current.resize(95)
                // Main panel must be 5% when right panel is 95% in mobile
                if (mainPanelRef.current) {
                    mainPanelRef.current.resize(5)
                }
            } else if (!isMobile) {
                // When switching to desktop, use the target size (may have been set to 95% for mobile)
                // Reset to a reasonable desktop size if it was 95%
                if (rightPanelTargetSizeRef.current === 95) {
                    rightPanelTargetSizeRef.current = DEFAULT_RIGHT_PANEL_SIZE
                    rightPanelRef.current.resize(DEFAULT_RIGHT_PANEL_SIZE)
                } else {
                    rightPanelRef.current.resize(rightPanelTargetSizeRef.current)
                }
                // Reset main panel to 75% for desktop
                if (mainPanelRef.current) {
                    mainPanelRef.current.resize(75)
                }
            }
        }
    }, [isMobile, open])

    // Track manual sidebar interactions
    React.useEffect(() => {
        if (!leftSidebarOpen) {
            if (!isLeftSidebarAutoCollapsed) {
                setIsLeftSidebarManuallyCollapsed(true)
            }
        } else {
            setIsLeftSidebarManuallyCollapsed(false)
            setIsLeftSidebarAutoCollapsed(false)
        }
    }, [leftSidebarOpen, isLeftSidebarAutoCollapsed])

    // Scroll to top when navigating to a stock/fund page
    // This ensures the page starts from the top after navigation from company dialog
    const prevPathnameRef = React.useRef(pathname)
    React.useEffect(() => {
        // Check if we've navigated to a stock or fund page
        const isStockOrFundRoute = pathname.startsWith('/stock/') || pathname.startsWith('/fund/')
        const wasStockOrFundRoute =
            prevPathnameRef.current.startsWith('/stock/') ||
            prevPathnameRef.current.startsWith('/fund/')

        // Only scroll if:
        // 1. We're on a stock/fund route, AND
        // 2. Either we just navigated TO a stock/fund route (wasn't on one before), OR
        // 3. We navigated to a different stock/fund (different base path)
        if (isStockOrFundRoute) {
            const currentBasePath = pathname.split('/').slice(0, 3).join('/') // e.g., /stock/nasdaq-aapl
            const prevBasePath = prevPathnameRef.current.split('/').slice(0, 3).join('/')

            // Scroll to top if we navigated to a different stock/fund or just entered a stock/fund route
            if (!wasStockOrFundRoute || currentBasePath !== prevBasePath) {
                // Use multiple frames to ensure the DOM has fully updated and content is rendered
                // This handles cases where Next.js restores scroll position after navigation
                const scrollToTop = () => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'instant' })
                    }
                }

                // Scroll immediately
                scrollToTop()

                // Scroll again after a short delay to handle any scroll restoration
                requestAnimationFrame(() => {
                    scrollToTop()
                    // Also scroll after a tiny delay to catch any late scroll restoration
                    setTimeout(scrollToTop, 50)
                })
            }
        }

        // Update the previous pathname
        prevPathnameRef.current = pathname
    }, [pathname])

    // Handle panel layout changes to auto-collapse left sidebar when right panel reaches maxSize
    const handlePanelLayout = React.useCallback(
        (sizes: number[]) => {
            // Track right panel size for handle visibility
            if (sizes.length >= 2 && sizes[1] !== undefined) {
                setRightPanelSize(sizes[1])
            }

            // Only auto-collapse if right panel is open
            if (!open) return

            // Don't auto-expand if the right panel just opened (prevents unwanted expansion on initial open)
            const shouldPreventAutoExpand = rightPanelJustOpenedRef.current

            // sizes array: [leftPanelSize, rightPanelSize]
            // Right panel is the second panel (index 1)
            if (sizes.length >= 2 && sizes[1] !== undefined) {
                const rightPanelSize = sizes[1]
                const COLLAPSE_THRESHOLD = 49.5 // Slight threshold to avoid flickering when collapsing
                const EXPAND_THRESHOLD = 30 // Require smaller right panel size before reopening left sidebar

                if (rightPanelSize >= COLLAPSE_THRESHOLD) {
                    // Right panel is at or near max size, collapse left sidebar
                    if (leftSidebarOpen) {
                        setLeftSidebarOpen(false)
                        setIsLeftSidebarAutoCollapsed(true)
                    }
                } else if (
                    rightPanelSize <= EXPAND_THRESHOLD &&
                    isLeftSidebarAutoCollapsed &&
                    !isLeftSidebarManuallyCollapsed &&
                    !shouldPreventAutoExpand // Don't auto-expand if panel just opened
                ) {
                    // Right panel is significantly smaller, expand left sidebar back
                    setLeftSidebarOpen(true)
                    setIsLeftSidebarAutoCollapsed(false)
                }
            }
        },
        [
            setLeftSidebarOpen,
            open,
            leftSidebarOpen,
            isLeftSidebarAutoCollapsed,
            isLeftSidebarManuallyCollapsed,
        ]
    )

    // Use the right sidebar content from state
    const finalRightSidebarContent = rightSidebarContent
    const finalRightSidebarShowCopy = rightSidebarShowCopyButton
    const finalRightSidebarTitle = rightSidebarTitle

    // Calculate panel sizes based on layout direction
    const mainPanelDefaultSize = React.useMemo(() => {
        if (isMobile) {
            // Vertical layout: when open and right panel is 95%, main panel should be 5%
            return open ? 5 : 100
        }
        // Horizontal layout: when open, main panel takes remaining space
        return open ? 75 : 100
    }, [isMobile, open])

    return (
        <div className="flex h-screen w-full bg-sidebar">
            <AppSidebar onSearchFocus={handleInputFocus} />
            <ResizablePanelGroup
                key={panelGroupKey}
                id={panelGroupId}
                direction={isMobile ? 'vertical' : 'horizontal'}
                className={`flex-1 ${isMobile ? 'mx-1' : ''}`}
                onLayout={handlePanelLayout}
            >
                <ResizablePanel
                    id={`${panelGroupId}-main`}
                    ref={mainPanelRef}
                    defaultSize={mainPanelDefaultSize}
                    minSize={isMobile ? 0 : 30}
                >
                    <MainContent
                        scrollContainerRef={scrollContainerRef}
                        rightSidebarContext={
                            <RightSidebarContentContext.Provider
                                value={{
                                    setRightSidebarContent,
                                    clearRightSidebarContent,
                                    setRightSidebarWidth,
                                }}
                            >
                                <AnalysisCacheProvider>{children}</AnalysisCacheProvider>
                            </RightSidebarContentContext.Provider>
                        }
                    >
                        {children}
                    </MainContent>
                </ResizablePanel>
                {open && rightPanelSize > 0 && (
                    <ResizableHandle withHandle className={`${isMobile ? 'my-1' : ''}`} />
                )}
                <ResizablePanel
                    id={`${panelGroupId}-right`}
                    ref={rightPanelRef}
                    defaultSize={0}
                    minSize={isMobile ? 25 : 25}
                    maxSize={isMobile ? 95 : 50}
                    collapsible={true}
                    style={
                        isAnimating
                            ? {
                                  transition:
                                      'flex-basis 500ms ease-in-out, flex-grow 500ms ease-in-out',
                              }
                            : undefined
                    }
                >
                    {shouldRenderContent && (
                        <RightSidebar
                            content={finalRightSidebarContent}
                            isMobile={isMobile}
                            showCopyButton={finalRightSidebarShowCopy}
                            title={finalRightSidebarTitle}
                        />
                    )}
                </ResizablePanel>
            </ResizablePanelGroup>

            {/* Company Dialog */}
            <CompanyDialog
                open={isCompanyDialogOpen}
                onOpenChange={setIsCompanyDialogOpen}
                showToggleButton={false} // Hide the toggle button
                onBodyClick={handleCompanyBodyClick}
            />

            {/* Upgrade Plan Dialog */}
            <UpdatePlanDialog
                currentPlan={plans.find((plan) => plan.id === 'basic') || plans[0]!} // Default to basic plan as current
                plans={plans}
                open={isUpgradeDialogOpen}
                onOpenChange={setIsUpgradeDialogOpen}
                onPlanChange={(planId) => {
                    // The payment flow is handled within the dialog component
                    // No need to close dialog here as payment redirect will handle navigation
                }}
                title="Choose Your Plan"
            />
        </div>
    )
}

function EmbeddedLayout({ children }: { children: React.ReactNode }) {
    // Send height updates to parent iframe
    useIframeResize()

    return (
        <LayoutProviders>
            <RightSidebarContentContext.Provider
                value={{
                    setRightSidebarContent: () => {},
                    clearRightSidebarContent: () => {},
                    setRightSidebarWidth: () => {},
                }}
            >
                <AnalysisCacheProvider>
                    <div className="w-full bg-background scrollbar-none">{children}</div>
                </AnalysisCacheProvider>
            </RightSidebarContentContext.Provider>
        </LayoutProviders>
    )
}

export function AppLayout({ children }: AppLayoutProps) {
    const pathname = usePathname()

    // Check if we're on a stock page
    const isStockPage = pathname.startsWith('/stock/')

    // Track embedded mode using hash fragment
    // Initialize synchronously to prevent flash of wrong layout
    const [embeddedMode, setEmbeddedMode] = React.useState(() => {
        if (typeof window === 'undefined') return false
        return isStockPage && window.location.hash === '#embedded'
    })

    // Monitor hash changes during navigation
    React.useEffect(() => {
        if (!isStockPage) return

        const checkEmbeddedMode = () => {
            const isEmbedded = window.location.hash === '#embedded'
            setEmbeddedMode(isEmbedded)
        }

        // Listen for hash changes (in case it changes during navigation)
        window.addEventListener('hashchange', checkEmbeddedMode)

        return () => {
            window.removeEventListener('hashchange', checkEmbeddedMode)
        }
    }, [isStockPage])

    // Render minimal layout for embedded iframe mode
    if (embeddedMode && isStockPage) {
        return <EmbeddedLayout>{children}</EmbeddedLayout>
    }

    // Authenticated layout with sidebar and right panel
    // All routes using AppLayout are protected by middleware
    return (
        <LayoutProviders>
            <AuthenticatedLayoutContent>{children}</AuthenticatedLayoutContent>
        </LayoutProviders>
    )
}
